import React, { useState, useEffect, useCallback } from 'react';
import { FaSave, FaTimes, FaUser, FaBuilding, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import '../../styles/AdminProfile.css';

const AdminProfile = () => {
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [userCompany, setUserCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [companyForm, setCompanyForm] = useState({
    name: '',
    email: '',
    contactPerson: '',
    phone: ''
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const token = sessionStorage.getItem('token');

  const fetchUserCompany = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/api/companies/user/company`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserCompany(data.company);
      } else {
        setUserCompany(null);
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      setUserCompany(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchUserCompany();
  }, [fetchUserCompany]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!companyForm.name || !companyForm.email) {
        setError('Company name and email are required');
        return;
      }

      const response = await fetch(`${apiUrl}/api/companies/user/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(companyForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create company');
      }

      const data = await response.json();
      setUserCompany(data.company);
      setSuccess('Company created successfully!');
      setShowCompanyForm(false);
      setCompanyForm({
        name: '',
        email: '',
        contactPerson: '',
        phone: ''
      });
    } catch (error) {
      console.error('Error creating company:', error);
      setError(error.message || 'Failed to create company');
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div className="admin-profile-container">
      <div className="profile-header">
        <h2>Admin Profile</h2>
        <p>Manage your profile and company information</p>
      </div>

      {error && (
        <div className="message error">
          {error}
        </div>
      )}

      {success && (
        <div className="message success">
          {success}
        </div>
      )}

      {/* User Info */}
      <div className="profile-section">
        <h3><FaUser /> Personal Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Name</label>
            <p>{user.name || 'N/A'}</p>
          </div>
          <div className="info-item">
            <label>Email</label>
            <p>{user.email || 'N/A'}</p>
          </div>
          <div className="info-item">
            <label>Role</label>
            <p>{user.role === 'admin' ? 'Lab Admin' : user.role}</p>
          </div>
          <div className="info-item">
            <label>Department</label>
            <p>{user.department || 'Not assigned'}</p>
          </div>
        </div>
      </div>

      {/* Company Details */}
      <div className="profile-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3><FaBuilding /> Company Details</h3>
          {userCompany && (
            <button 
              onClick={fetchUserCompany}
              style={{
                padding: '10px 18px',
                backgroundColor: '#0369a1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
                boxShadow: '0 2px 6px rgba(3, 105, 161, 0.15)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#025a82';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(3, 105, 161, 0.25)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#0369a1';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(3, 105, 161, 0.15)';
              }}
            >
              <FaBuilding/> Refresh
            </button>
          )}
        </div>

        {userCompany ? (
          <div className="company-info">
            <div className="company-info-title"><FaCheckCircle /> Company Information</div>
            <div className="company-details-grid">
              <div className="company-detail-item">
                <label>Company Name</label>
                <p>{userCompany.name}</p>
              </div>
              <div className="company-detail-item">
                <label>Email</label>
                <p>{userCompany.email}</p>
              </div>
              <div className="company-detail-item">
                <label>Contact Person</label>
                <p>{userCompany.contactPerson || 'Not provided'}</p>
              </div>
              <div className="company-detail-item">
                <label>Phone</label>
                <p>{userCompany.phone || 'Not provided'}</p>
              </div>
              <div className="company-detail-item">
                <label>Current Plan</label>
                <p style={{ color: '#0369a1', fontWeight: '700' }}>{userCompany.plan}</p>
              </div>
              <div className="company-detail-item">
                <label>Monthly Price</label>
                <p>${userCompany.price}/mo</p>
              </div>
              <div className="company-detail-item">
                <label>Users Limit</label>
                <p>{userCompany.users}</p>
              </div>
              <div className="company-detail-item">
                <label>Assets Limit</label>
                <p>{userCompany.assets}</p>
              </div>
              <div className="company-detail-item">
                <label>Status</label>
                <p style={{ color: userCompany.status === 'active' ? '#10b981' : '#ef4444', fontWeight: '700' }}>
                  {userCompany.status?.toUpperCase()}
                </p>
              </div>
              <div className="company-detail-item">
                <label>Days Remaining</label>
                <p style={{ color: userCompany.daysRemaining < 30 ? '#ef4444' : '#10b981', fontWeight: '700' }}>
                  {userCompany.daysRemaining} days
                </p>
              </div>
            </div>
          </div>
        ) : showCompanyForm ? (
          <form onSubmit={handleSubmit} className="company-form">
            <h4>Add Company Details</h4>
            <div className="form-groups-row">
              <div className="form-group">
                <label>Company Name *</label>
                <input 
                  type="text"
                  name="name"
                  value={companyForm.name}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Company Email *</label>
                <input 
                  type="email"
                  name="email"
                  value={companyForm.email}
                  onChange={handleInputChange}
                  placeholder="Enter company email"
                  required
                />
              </div>
            </div>

            <div className="form-groups-row">
              <div className="form-group">
                <label>Contact Person</label>
                <input 
                  type="text"
                  name="contactPerson"
                  value={companyForm.contactPerson}
                  onChange={handleInputChange}
                  placeholder="Enter contact person name"
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input 
                  type="tel"
                  name="phone"
                  value={companyForm.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            <div className="button-group">
              <button 
                type="submit"
                className="btn btn-primary"
              >
                <FaSave /> Save Company
              </button>
              <button 
                type="button"
                onClick={() => setShowCompanyForm(false)}
                className="btn btn-secondary"
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="company-form" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p style={{ marginBottom: '20px', color: '#666', fontSize: '15px' }}><FaExclamationCircle style={{ marginRight: '6px' }} /> No company details added yet.</p>
            <button 
              onClick={() => setShowCompanyForm(true)}
              className="btn btn-primary"
            >
              + Add Company Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProfile;
